import React from 'react'
import { Autocomplete, Box, TextField, Tooltip } from '@mui/material'
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined'
import { useCollectionData, useCollectionDataOnce } from 'react-firebase-hooks/firestore'
import { collection, query, where } from 'firebase/firestore'
import { firebase_firestore } from '../../common/firebaseConfig'
import { useApplicationSuiteContext } from '../../contexts/ApplicationSuiteProvider'

type PatternSelectionProps = {
  pattern?: any
  onSelect: (pattern: any) => void
  error?: {
    hasError: boolean
    message?: string
  }
  disabled?: boolean
  helperText?: string
  // For callers that can only resolve a standard pattern by id (e.g. alert templates);
  // offering personal patterns there would let the caller pick an id it can never load.
  standardOnly?: boolean
}

function PatternSelect({
  pattern,
  onSelect,
  error,
  helperText,
  disabled = false,
  standardOnly = false,
}: PatternSelectionProps) {
  const { usermeta, tradingHouse } = useApplicationSuiteContext()

  const [usermetaPatterns] = useCollectionData(
    standardOnly
      ? undefined
      : query(
          collection(firebase_firestore, `usermeta/${usermeta.gcid}/patterns`),
          where('active', '==', true),
          where('tradingHouse', '==', tradingHouse)
        )
  )

  const [standardPatterns] = useCollectionDataOnce(
    query(
      collection(firebase_firestore, `tradingHouses/${tradingHouse}/standardPatterns`),
      where('active', '==', true)
    )
  ) as any[]

  // Handle pattern selection locally and update context
  const handlePatternSelect = (newPattern: any) => {
    onSelect(newPattern)
  }

  return (
    <Autocomplete
      disabled={disabled}
      fullWidth
      id="tags-filled"
      onChange={(event: any, newValue: any) => handlePatternSelect(newValue)}
      getOptionLabel={(option) => `${option.name} (v${option.version})`}
      options={(standardPatterns || [])
        .sort((a: any, b: any) => a.name.localeCompare(b.name))
        .concat((usermetaPatterns || []).sort((a: any, b: any) => a.name.localeCompare(b.name)))}
      value={pattern || null}
      renderOption={(props, option, { selected }) => (
        <li {...props} key={option.id} style={{ width: '100%' }}>
          <Box display="flex" alignItems="flex-end" width={'100%'}>
            <Box display="flex" alignItems="center" gap={0.5}>
              {option.name}{' '}
              {option.author && (
                <span style={{ fontSize: 10, color: 'gray' }}>({option.author})</span>
              )}
              {option.source === 'strategy-agent' && (
                <Tooltip title="Created by AI Session">
                  <SmartToyOutlinedIcon
                    sx={{ fontSize: 12, color: 'text.disabled', verticalAlign: 'middle' }}
                  />
                </Tooltip>
              )}
            </Box>

            <span
              style={{
                flexGrow: 1,
                backgroundImage:
                  'radial-gradient(gray 15%, transparent 20%)' /* This makes actual dots */,
                backgroundSize: '5px 1px' /* Adjust the size of dots and the gap between them */,
                height: '1px' /* The height of the line of dots */,
                margin:
                  '0 10px' /* Optional: to give some space between name/dots and dots/number */,
              }}
            />
            <Box sx={{ fontSize: '10px' }}> v{option.version}</Box>
          </Box>
        </li>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          variant="outlined"
          label="Pattern"
          error={!!error?.hasError}
          helperText={error?.hasError ? error.message : helperText}
          placeholder=""
          FormHelperTextProps={{
            sx: {
              mx: 1,
            },
          }}
        />
      )}
    />
  )
}

export default PatternSelect
